import React from "react";
import { Box } from "@mui/joy";
import type { SMSPlaceholderDefinition } from "api/smsTemplates/SMSTemplate.typed";
import { colorTokens, textTokens } from "styles/globalStyles";

const PH_RE = /\{\{\s*\.?([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;
const CHIP_ATTR = "data-sms-ph";
const CHIP_KEY_ATTR = "data-sms-ph-key";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function labelForKey(key: string, placeholders: readonly SMSPlaceholderDefinition[]): string {
  const found = placeholders.find((p) => p.key === key);
  const label = found?.label?.trim();

  return label && label.length > 0 ? label : key;
}

function chipHtml(key: string, label: string): string {
  return (
    `<span ${CHIP_ATTR}="1" ${CHIP_KEY_ATTR}="${escapeHtml(key)}" ` +
    `contenteditable="false" class="zekay-sms-ph-chip">${escapeHtml(label)}</span>`
  );
}

export function smsTemplateToEditorHtml(
  template: string,
  placeholders: readonly SMSPlaceholderDefinition[],
): string {
  if (!template) return "";

  let html = "";
  let last = 0;
  const re = new RegExp(PH_RE.source, "g");
  let match: RegExpExecArray | null;

  while ((match = re.exec(template)) !== null) {
    if (match.index > last) {
      html += escapeHtml(template.slice(last, match.index)).replace(/\n/g, "<br>");
    }
    const key = match[1];
    html += chipHtml(key, labelForKey(key, placeholders));
    last = match.index + match[0].length;
  }

  if (last < template.length) {
    html += escapeHtml(template.slice(last)).replace(/\n/g, "<br>");
  }

  return html;
}

export function smsEditorHtmlToTemplate(root: HTMLElement): string {
  let out = "";

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent ?? "";

      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;

    if (el.getAttribute(CHIP_ATTR) === "1") {
      const key = el.getAttribute(CHIP_KEY_ATTR)?.trim();
      if (key) out += `{{.${key}}}`;

      return;
    }

    if (el.tagName === "BR") {
      out += "\n";

      return;
    }

    if (el.tagName === "DIV" || el.tagName === "P") {
      if (out.length > 0 && !out.endsWith("\n")) out += "\n";
    }

    for (const child of Array.from(el.childNodes)) {
      walk(child);
    }
  };

  for (const child of Array.from(root.childNodes)) {
    walk(child);
  }

  return out.replace(/\u00a0/g, " ");
}

export type SMSMessageChipEditorHandle = {
  insertPlaceholder: (key: string) => void;
  insertText: (text: string) => void;
  focus: () => void;
};

type SMSMessageChipEditorProps = {
  value: string;
  placeholders: SMSPlaceholderDefinition[];
  onChange: (template: string) => void;
  disabled?: boolean;
  minRows?: number;
  maxHeight?: number | string;
  placeholder?: string;
};

const smsChipEditorSx = {
  minHeight: 96,
  overflow: "auto",
  overscrollBehavior: "contain",
  px: 1.25,
  py: 1,
  borderRadius: "8px",
  border: `1px solid ${colorTokens.border.default}`,
  bgcolor: "transparent",
  color: textTokens.color.primary,
  fontSize: textTokens.size.sm,
  lineHeight: 1.5,
  outline: "none",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  "&:focus": {
    borderColor: colorTokens.accent.primary,
  },
  "&:empty::before": {
    content: "attr(data-placeholder)",
    color: textTokens.color.muted,
    pointerEvents: "none",
  },
  [`& .zekay-sms-ph-chip`]: {
    display: "inline-flex",
    alignItems: "center",
    verticalAlign: "baseline",
    mx: "1px",
    px: 0.75,
    py: 0.15,
    borderRadius: "999px",
    border: `1px solid ${colorTokens.border.strong}`,
    bgcolor: colorTokens.bg.surfaceMuted,
    color: colorTokens.accent.primary,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.4,
    userSelect: "none",
    cursor: "default",
  },
} as const;

const SMSMessageChipEditor = React.forwardRef<
  SMSMessageChipEditorHandle,
  SMSMessageChipEditorProps
>(function SMSMessageChipEditor(props, ref) {
  const {
    value,
    placeholders,
    onChange,
    disabled,
    minRows = 4,
    maxHeight = 220,
    placeholder,
  } = props;
  const editorRef = React.useRef<HTMLDivElement | null>(null);
  const lastEmitted = React.useRef<string | null>(null);
  const placeholdersRef = React.useRef(placeholders);
  const valueRef = React.useRef(value);
  placeholdersRef.current = placeholders;
  valueRef.current = value;

  const emitFromDom = React.useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = smsEditorHtmlToTemplate(el);
    if (next === lastEmitted.current) return;
    lastEmitted.current = next;
    onChange(next);
  }, [onChange]);

  React.useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    el.innerHTML = smsTemplateToEditorHtml(value, placeholdersRef.current) || "";
  }, [value]);

  React.useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    // Only refresh chip labels when the catalog identity changes after first paint.
    if (lastEmitted.current == null) return;
    const body = valueRef.current;
    el.innerHTML = smsTemplateToEditorHtml(body, placeholders) || "";
    lastEmitted.current = body;
  }, [placeholders]);

  const insertHtmlAtCursor = React.useCallback(
    (html: string) => {
      const el = editorRef.current;
      if (!el || disabled) return;
      el.focus();
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
        el.innerHTML = (el.innerHTML || "") + html;
        emitFromDom();

        return;
      }
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const holder = document.createElement("div");
      holder.innerHTML = html;
      const frag = document.createDocumentFragment();
      let lastNode: ChildNode | null = null;
      while (holder.firstChild) {
        lastNode = holder.firstChild;
        frag.appendChild(lastNode);
      }
      range.insertNode(frag);
      if (lastNode) {
        range.setStartAfter(lastNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      emitFromDom();
    },
    [disabled, emitFromDom],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      insertPlaceholder: (key: string) => {
        insertHtmlAtCursor(chipHtml(key, labelForKey(key, placeholdersRef.current)));
      },
      insertText: (text: string) => {
        insertHtmlAtCursor(escapeHtml(text).replace(/\n/g, "<br>"));
      },
      focus: () => editorRef.current?.focus(),
    }),
    [insertHtmlAtCursor],
  );

  return (
    <Box
      ref={editorRef}
      role="textbox"
      aria-multiline
      aria-label={placeholder || "Message SMS"}
      contentEditable={!disabled}
      suppressContentEditableWarning
      onInput={emitFromDom}
      onBlur={emitFromDom}
      data-placeholder={placeholder || "Message SMS…"}
      sx={{
        ...smsChipEditorSx,
        minHeight: minRows * 24,
        maxHeight,
        ...(maxHeight === "100%"
          ? { height: "100%", flex: 1, minHeight: Math.max(minRows * 24, 120) }
          : {}),
      }}
    />
  );
});

export default SMSMessageChipEditor;
